import GlobalPrototypes from 'src/config/services/globalPrototypes.ts'
import { defineRule , configure } from 'vee-validate'
import { required, min, max, min_value, max_value, is, url, email, regex, numeric, length } from '@vee-validate/rules'
import { setLocale, localize } from '@vee-validate/i18n'
import { getDictionary , i18n } from 'src/config/i18n.ts'

const loadedVeeValidateDefaultMessages = {}

// WARNING: Due to how VeeValidate exports its locale files, we can't use dynamic async import here.
// It would result in build time errors "module not found/exported"
// Due to how @vee-validate/i18n is structured/built, we can't dynamically import with
// import.meta.glob('/node_modules/@vee-validate/i18n/dist/locale/*.json')
// Instead we use explicit imports (same pattern as moment.ts)
const loadVeeValidateDefaultMessages = async (lang) => {
  // if the locale was already imported before, don't load it again
  if (loadedVeeValidateDefaultMessages[lang]) {
    return loadedVeeValidateDefaultMessages[lang]
  }

  switch (lang) {
  case 'de':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/de.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'en':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/en.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'es':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/es.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'tr':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/tr.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'uk':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/uk.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'fr':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/fr.json')
    return loadedVeeValidateDefaultMessages[lang]
  case 'it':
    loadedVeeValidateDefaultMessages[lang] = await import('@vee-validate/i18n/dist/locale/it.json')
    return loadedVeeValidateDefaultMessages[lang]
  default:
    throw new Error(`Unsupported locale: ${lang}`)
  }
}

export const switchVeeValidateLocale = async (targetLocale) => {
  // Load VeeValidate default messages for target locale
  const veeValidateDefaultMessages = await loadVeeValidateDefaultMessages(targetLocale)

  // Load our own custom messages for target locale
  // getDictionary() may return undefined for a locale if its messages haven't been loaded yet
  const sduiVeeValidateMessages = getDictionary()[targetLocale] || {}

  // Resolve values through i18n.global.t() so that variables in language keys (@:{...}) are resolved correctly.
  const translatedAttributeNames = Object.fromEntries(
    Object.keys(sduiVeeValidateMessages.attributes || {}).map((key) => [
      key,
      i18n.global.t(`validations.attributes.${key}`)
    ])
  )

  // Tell VeeValidate to use both the default messages and our custom messages
  configure({
    generateMessage: localize(targetLocale, {
      messages: {
        ...veeValidateDefaultMessages.messages,
      },
      fields: {
        ...sduiVeeValidateMessages.custom
      },
      names: {
        ...veeValidateDefaultMessages.names,
        ...translatedAttributeNames,
      },
    }),
  })

  // Finally, set the locale for VeeValidate
  setLocale(targetLocale)
}

export const setupVeeValidate = () => {
  // Register validation rules provided by vee-validate (https://vee-validate.logaretm.com/v4/guide/global-validators#available-rules)
  // We only import rules that are used in the app to reduce bundle size.
  defineRule('required', required)
  defineRule('min', min)
  defineRule('max', max)
  defineRule('min_value', min_value)
  defineRule('max_value', max_value)
  defineRule('is', is)
  defineRule('url', url)
  defineRule('email', email)
  defineRule('regex', regex)
  defineRule('numeric', numeric)
  defineRule('length', length)

  const getErrorMessage = (ctx) => {
    const topic = ctx.label ? ctx.label : ctx.name
    const ruleName = ctx.rule.name

    // if translation does not exist, return the default error message ("FOO is invalid")
    if (!i18n.global.te(`validations.custom.${topic}.${ruleName}`)) {
      console.warn(`[Validation] Custom rule "${ruleName}" missing translation: "${ruleName}" for "${topic}" does not have a translation (key: "validations.custom.${topic}.${ruleName}")\n\nAdd it to the i18n dictionary if it's missing.`)
      return false
    }
    return i18n.global.t(`validations.custom.${topic}.${ruleName}`)
  }

  // Register custom validation rules
  defineRule('isSameOrAfter', (value, [dateToCompare], ctx) => {
    if (!value) return false
    if (!dateToCompare || !GlobalPrototypes.moment(dateToCompare).isValid()) return true
    if (GlobalPrototypes.moment(value).isSameOrAfter(GlobalPrototypes.moment(dateToCompare))) return true
    return getErrorMessage(ctx)
  })

  defineRule('isSameOrBefore', (value, [dateToCompare], ctx) => {
    if (!value) return false
    if (!dateToCompare || !GlobalPrototypes.moment(dateToCompare).isValid()) return true
    if (GlobalPrototypes.moment(value).isSameOrBefore(GlobalPrototypes.moment(dateToCompare))) return true
    return getErrorMessage(ctx)
  })

  defineRule('isAfter', (value, [dateToCompare], ctx) => {
    if (!value) return false
    if (!dateToCompare || !GlobalPrototypes.moment(dateToCompare).isValid()) return true
    if (GlobalPrototypes.moment(value).isAfter(GlobalPrototypes.moment(dateToCompare))) return true
    return getErrorMessage(ctx)
  })

  defineRule('isBefore', (value, [dateToCompare], ctx) => {
    if (!value) return false
    if (!dateToCompare || !GlobalPrototypes.moment(dateToCompare).isValid()) return true
    if (GlobalPrototypes.moment(value).isBefore(GlobalPrototypes.moment(dateToCompare))) return true
    return getErrorMessage(ctx)
  })

  defineRule('isTimeAfter', (value, [timeToCompare], ctx) => {
    if (!value) return false
    if (!timeToCompare) return true
    const beginVal = Number(value.replace(':', ''))
    const endsCompareVal = Number(timeToCompare.replace(':', ''))
    if (beginVal > endsCompareVal) return true
    return getErrorMessage(ctx)
  })

  defineRule('isTimeBefore', (value, [timeToCompare], ctx) => {
    if (!value) return false
    if (!timeToCompare) return true
    const endVal = Number(value.replace(':', ''))
    const beginCompareVal = Number(timeToCompare.replace(':', ''))
    if (endVal < beginCompareVal) return true
    return getErrorMessage(ctx)
  })

  defineRule('isInSameYear', (value, [dateToCompare], ctx) => {
    if (!value) return false
    if (!dateToCompare || !GlobalPrototypes.moment(dateToCompare).isValid()) return true
    if (GlobalPrototypes.moment(value).year() !== GlobalPrototypes.moment(dateToCompare).year()) return true
    return getErrorMessage(ctx)
  })

  defineRule('lengthNoSpaces', (value, [length], ctx) => {
    if (!value) return false
    if (value.replace(/\s+/g, '').length === Number(length)) return true
    return getErrorMessage(ctx)
  })

  defineRule('isBase32', (value, _, ctx) => {
    if (!value) return false
    const base32Regex = /^[A-Z2-7]+=*$/i
    if (base32Regex.test(value.replace(/\s+/g, ''))) return true
    return getErrorMessage(ctx)
  })

  defineRule('isPhoneNumber', (value, _, ctx) => {
    if (!value) return true
    const phoneRegex = /^(((00\d{2})|(0\d{3})\d{4,20})|(\+\d[^\\+00])\d{4,20})$/
    if (phoneRegex.test(value.replace(/\s|\(|\)/g, ''))) return true
    return getErrorMessage(ctx)
  })

  defineRule('date_format', (value, [format], ctx) => {
    if (!value) return true
    if (GlobalPrototypes.moment(value, format, true).isValid()) return true
    return getErrorMessage(ctx)
  })
}
